import { existsSync, promises as fs } from 'node:fs'
import path from 'node:path'

import { spawn } from '@socketsecurity/registry/lib/spawn'

import { assembleFacts } from './assemble.mts'
import { resolveBuildToolBin, socketFactsFileName } from './build-tool.mts'
import { serializeExcludePathPatterns } from './exclude-paths-glob.mts'
import { parseRecords } from './records.mts'
import constants from '../../../constants.mts'
import { withTmpDir } from '../../../utils/fs.mts'

import type { BuildTool } from './build-tool.mts'
import type {
  ResolvedArtifactPaths,
  SocketFactsSbom,
  SocketFactsSbomProject,
} from './facts.mts'
import type { ResolutionReport } from './resolution-report.mts'

export type ManifestScriptOptions = {
  projectDir: string
  // Unset ⇒ resolved to the project wrapper, else PATH (resolveBuildToolBin).
  bin?: string | undefined
  // Reachability-only: also materialize resolved artifact paths (artifactPaths).
  withFiles?: boolean | undefined
  // sbt only: caller-supplied directory to use as sbt's isolated global base.
  // sbt provisions the project's Scala toolchain (compiler/library/reflect)
  // under `<global base>/boot`, which withFiles' artifactPaths point into, so
  // the directory must outlive this call; the caller owns creating it and
  // deleting it once those paths are no longer needed. Unset ⇒ an ephemeral
  // dir is created and cleaned up before this call returns (the withFiles-less
  // default, and the case for a standalone `socket manifest sbt --facts`).
  tmpDir?: string | undefined
  // Newline-delimited GAV file scoping withFiles materialization; absent ⇒ all.
  populateFilesFor?: string | undefined
  includeConfigs?: string | undefined
  excludeConfigs?: string | undefined
  // Scan-root-relative `--exclude-paths`. Passed to the build script, which skips
  // resolving a wholly excluded subproject. Source-file-level exclusion is left to
  // the reachability analysis (coana's --exclude-dirs), not applied here.
  excludePaths?: string[] | undefined
  toolOpts?: string[] | undefined
  stdio?: 'inherit' | 'pipe' | undefined
  env?: NodeJS.ProcessEnv | undefined
  signal?: AbortSignal | undefined
}

export type ManifestRunResult = {
  code: number
  facts: SocketFactsSbom
  // Undefined when the build did not report it.
  buildRoot: string | undefined
  // Undefined when a file-addressed build did not report its entry file.
  factsFileName: string | undefined
  report: ResolutionReport
  artifactPaths: ResolvedArtifactPaths
  // Captured build-tool output (empty when stdio is 'inherit').
  stderr: string
  stdout: string
}

type RunOutput = { code: number; stdout: string; stderr: string }

const FACTS_TASK = 'socketFacts'
const WORKSPACES_TASK = 'socketWorkspaces'
// Destination filename inside sbt's isolated plugins/ dir; unrelated to the source filename, which
// is whatever socket-facts.plugin.scala / socket-workspaces.plugin.scala are called on disk.
const SBT_FACTS_PLUGIN_FILENAME = 'SocketFactsPlugin.scala'
const SBT_WORKSPACES_PLUGIN_FILENAME = 'SocketWorkspacesPlugin.scala'

export type WorkspaceEnumerationResult = {
  code: number
  projects: SocketFactsSbomProject[]
  stderr: string
  stdout: string
}

// Bundled emitter assets, copied into dist by the rollup build.
function manifestScriptsPath(...parts: string[]): string {
  return path.join(constants.distPath, 'manifest-scripts', ...parts)
}

// Don't throw on a non-zero exit: the script emits failure records, so a usable
// records file still exists. A non-exit spawn error (e.g. missing executable)
// propagates.
async function runNeverThrow(
  bin: string,
  args: string[],
  opts: ManifestScriptOptions,
): Promise<RunOutput> {
  try {
    const result = await spawn(bin, args, {
      cwd: opts.projectDir,
      stdio: opts.stdio ?? 'inherit',
      ...(opts.env ? { env: opts.env } : {}),
      ...(opts.signal ? { signal: opts.signal } : {}),
    })
    return {
      code: result.code,
      stdout: typeof result.stdout === 'string' ? result.stdout : '',
      stderr: typeof result.stderr === 'string' ? result.stderr : '',
    }
  } catch (e) {
    // A build tool that exits non-zero rejects with the spawn-result shape: a
    // numeric exit `code` plus captured stdout/stderr. Return it so the caller
    // can assemble failure records / surface the output. Anything else — e.g. a
    // missing executable, whose `code` is a string like 'ENOENT' — propagates.
    // This mirrors how utils/dlx.mts classifies spawn failures (a numeric `code`
    // is a real process exit); the registry's isSpawnError is avoided here
    // because it is currently broken and never matches.
    if (
      e !== null &&
      typeof e === 'object' &&
      typeof (e as { code?: unknown }).code === 'number'
    ) {
      const err = e as { code: number; stdout?: unknown; stderr?: unknown }
      return {
        code: err.code,
        stdout: typeof err.stdout === 'string' ? err.stdout : '',
        stderr: typeof err.stderr === 'string' ? err.stderr : '',
      }
    }
    throw e
  }
}

async function writeSbtPlugin(
  globalBase: string,
  sourceFilename: string,
  destFilename: string,
): Promise<void> {
  const src = await fs.readFile(manifestScriptsPath(sourceFilename), 'utf8')
  const pluginsDir = path.join(globalBase, 'plugins')
  await fs.mkdir(pluginsDir, { recursive: true })
  await fs.writeFile(path.join(pluginsDir, destFilename), src)
}

async function assembleFromRecords(
  tool: BuildTool,
  out: RunOutput,
  recordsFile: string,
): Promise<ManifestRunResult> {
  const text = existsSync(recordsFile)
    ? await fs.readFile(recordsFile, 'utf8')
    : ''
  const parsed = parseRecords(text)
  const factsFileName = socketFactsFileName(tool, parsed.entry || undefined)
  const { artifactPaths, facts, report } = assembleFacts(parsed)
  return {
    buildRoot: parsed.buildRoot || undefined,
    code: out.code,
    facts,
    factsFileName,
    report,
    artifactPaths,
    stderr: out.stderr,
    stdout: out.stdout,
  }
}

// Missing only in an unbuilt local checkout. Fail loudly: without the extension,
// Maven silently emits an empty SBOM.
function assertMavenExtensionBuilt(jarPath: string): void {
  if (existsSync(jarPath)) {
    return
  }
  throw new Error(
    `Maven manifest extension jar not found at ${jarPath}. It is bundled in the published CLI; for local dev build it with: bash src/commands/manifest/scripts/maven-extension/build-jar.sh`,
  )
}

// Runs the build-tool script (which emits a records file) and assembles it.
// Writes no files; the caller persists facts or consumes artifactPaths.
export async function runManifestScript(
  tool: BuildTool,
  opts: ManifestScriptOptions,
): Promise<ManifestRunResult> {
  switch (tool) {
    case 'gradle':
      return await runGradle(opts)
    case 'sbt':
      return await runSbt(opts)
    case 'maven':
      return await runMaven(opts)
  }
}

// Emits only `project` records, never resolving dependencies, for discovery
// before committing to a full `runManifestScript` resolution cost.
export async function enumerateWorkspaces(
  tool: BuildTool,
  opts: ManifestScriptOptions,
): Promise<WorkspaceEnumerationResult> {
  const result = await (tool === 'gradle'
    ? enumerateGradleWorkspaces(opts)
    : tool === 'sbt'
      ? enumerateSbtWorkspaces(opts)
      : enumerateMavenWorkspaces(opts))
  return {
    code: result.code,
    projects: result.facts.projects ?? [],
    stderr: result.stderr,
    stdout: result.stdout,
  }
}

function commonProps(
  opts: ManifestScriptOptions,
  prefix: '-D' | '-P',
): string[] {
  const props: string[] = []
  if (opts.withFiles) {
    props.push(`${prefix}socket.withFiles=true`)
  }
  if (opts.populateFilesFor) {
    props.push(`${prefix}socket.populateFilesFor=${opts.populateFilesFor}`)
  }
  if (opts.includeConfigs) {
    props.push(`${prefix}socket.includeConfigs=${opts.includeConfigs}`)
  }
  if (opts.excludeConfigs) {
    props.push(`${prefix}socket.excludeConfigs=${opts.excludeConfigs}`)
  }
  // Globs compile to regex pattern sources HERE (exclude-paths-glob.mts is
  // the single glob implementation); the scripts just Pattern.compile what
  // they receive.
  const excludePatterns = serializeExcludePathPatterns(opts.excludePaths)
  if (excludePatterns) {
    props.push(`${prefix}socket.excludePaths=${excludePatterns}`)
  }
  return props
}

async function invokeGradle(
  opts: ManifestScriptOptions,
  initScriptFilename: string,
  task: string,
  tmpDirPrefix: string,
): Promise<ManifestRunResult> {
  const initScript = manifestScriptsPath(initScriptFilename)
  return await withTmpDir(tmpDirPrefix, async tmp => {
    const recordsFile = path.join(tmp, 'records.tsv')
    const bin = resolveBuildToolBin('gradle', opts.projectDir, opts.bin)
    // Disable the configuration cache: the init script's legacy
    // resolvedConfiguration API and shared accumulator aren't cache-safe.
    const args = [
      '--init-script',
      initScript,
      '-Dorg.gradle.configuration-cache=false',
      `-Psocket.recordsFile=${recordsFile}`,
      ...commonProps(opts, '-P'),
      ...(opts.toolOpts ?? []),
      task,
      '--no-daemon',
      '--console=plain',
    ]
    const out = await runNeverThrow(bin, args, opts)
    return await assembleFromRecords('gradle', out, recordsFile)
  })
}

async function runGradle(
  opts: ManifestScriptOptions,
): Promise<ManifestRunResult> {
  return await invokeGradle(
    opts,
    'socket-facts.init.gradle',
    FACTS_TASK,
    'socket-gradle-facts-',
  )
}

async function enumerateGradleWorkspaces(
  opts: ManifestScriptOptions,
): Promise<ManifestRunResult> {
  return await invokeGradle(
    opts,
    'socket-workspaces.init.gradle',
    WORKSPACES_TASK,
    'socket-gradle-workspaces-',
  )
}

async function invokeSbtIn(
  globalBase: string,
  opts: ManifestScriptOptions,
  pluginSourceFilename: string,
  pluginDestFilename: string,
  task: string,
): Promise<ManifestRunResult> {
  await writeSbtPlugin(globalBase, pluginSourceFilename, pluginDestFilename)
  const recordsFile = path.join(globalBase, 'records.tsv')
  const bin = resolveBuildToolBin('sbt', opts.projectDir, opts.bin)
  // Fresh per-run global base (not ~/.sbt): sbt executes everything under
  // plugins/, so a shared path is a code-injection surface. BSP off for this
  // run. sbt also provisions the project's Scala toolchain under
  // <global.base>/boot; when opts.tmpDir was supplied, the caller keeps this
  // directory (and that toolchain) alive until it's done with withFiles'
  // artifactPaths, so no special-casing is needed here.
  const props = [
    `-Dsbt.global.base=${globalBase}`,
    '-Dsbt.server.autostart=false',
    `-Dsocket.recordsFile=${recordsFile}`,
    ...commonProps(opts, '-D'),
  ]
  // sbt's launcher doesn't always honor JAVA_HOME; never override a
  // caller-supplied --java-home.
  const javaHome = opts.env?.['JAVA_HOME'] ?? process.env['JAVA_HOME']
  const javaHomeOpt =
    javaHome && !(opts.toolOpts ?? []).includes('--java-home')
      ? ['--java-home', javaHome]
      : []
  const args = [
    ...javaHomeOpt,
    ...props,
    ...(opts.toolOpts ?? []),
    '--batch',
    task,
  ]
  const out = await runNeverThrow(bin, args, opts)
  return await assembleFromRecords('sbt', out, recordsFile)
}

async function runSbt(opts: ManifestScriptOptions): Promise<ManifestRunResult> {
  const run = (globalBase: string) =>
    invokeSbtIn(
      globalBase,
      opts,
      'socket-facts.plugin.scala',
      SBT_FACTS_PLUGIN_FILENAME,
      FACTS_TASK,
    )
  if (opts.tmpDir) {
    return await run(opts.tmpDir)
  }
  return await withTmpDir('socket-sbt-facts-', run)
}

async function enumerateSbtWorkspaces(
  opts: ManifestScriptOptions,
): Promise<ManifestRunResult> {
  const run = (globalBase: string) =>
    invokeSbtIn(
      globalBase,
      opts,
      'socket-workspaces.plugin.scala',
      SBT_WORKSPACES_PLUGIN_FILENAME,
      WORKSPACES_TASK,
    )
  if (opts.tmpDir) {
    return await run(opts.tmpDir)
  }
  return await withTmpDir('socket-sbt-workspaces-', run)
}

async function invokeMaven(
  opts: ManifestScriptOptions,
  coanaTask: string,
  tmpDirPrefix: string,
): Promise<ManifestRunResult> {
  const jarPath = manifestScriptsPath(
    'maven-extension',
    'coana-maven-extension.jar',
  )
  assertMavenExtensionBuilt(jarPath)
  return await withTmpDir(tmpDirPrefix, async tmp => {
    const recordsFile = path.join(tmp, 'records.tsv')
    const bin = resolveBuildToolBin('maven', opts.projectDir, opts.bin)
    // `validate` is the cheapest phase that triggers the extension; no compile
    // needed (analysis uses configured paths, not classes).
    const props = [
      `-Dmaven.ext.class.path=${jarPath}`,
      `-Dcoana.task=${coanaTask}`,
      `-Dsocket.recordsFile=${recordsFile}`,
      ...commonProps(opts, '-D'),
    ]
    const args = [
      ...props,
      ...(opts.toolOpts ?? []),
      '--batch-mode',
      'validate',
    ]
    const out = await runNeverThrow(bin, args, opts)
    return await assembleFromRecords('maven', out, recordsFile)
  })
}

async function runMaven(
  opts: ManifestScriptOptions,
): Promise<ManifestRunResult> {
  return await invokeMaven(opts, 'socket-facts', 'socket-maven-facts-')
}

async function enumerateMavenWorkspaces(
  opts: ManifestScriptOptions,
): Promise<ManifestRunResult> {
  return await invokeMaven(
    opts,
    'socket-workspaces',
    'socket-maven-workspaces-',
  )
}
